<template>
  <div ref="rootRef" class="relative">
    <label v-if="label" class="form-label" :for="inputId">
      {{ label }}<span v-if="required" class="text-red-500"> *</span>
    </label>

    <div
      class="flex min-h-[42px] w-full items-stretch overflow-hidden rounded-sm border bg-white shadow-sm transition-colors"
      :class="[
        open ? 'border-indigo-400 ring-3 ring-indigo-100' : 'border-gray-200',
        errors.length ? 'border-red-500 ring-red-200' : '',
      ]"
    >
      <button
        :id="inputId"
        type="button"
        class="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left"
        :aria-expanded="open"
        aria-haspopup="listbox"
        @click="toggleOpen"
      >
        <template v-if="selectedPage">
          <span class="truncate font-medium text-gray-900">
            {{ selectedPage.title || selectedPage.label }}
          </span>
          <site-context-badge
            :tag="selectedPage.context_tag"
            :color-token="selectedPage.context_color"
          />
        </template>
        <span v-else class="text-gray-500">
          {{ placeholder }}
        </span>
      </button>

      <button
        v-if="modelValue != null && modelValue !== ''"
        type="button"
        class="shrink-0 border-l border-gray-200 px-3 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
        aria-label="Wyczyść wybór"
        @click.stop="clear"
      >
        ×
      </button>

      <button
        type="button"
        class="shrink-0 border-l border-gray-200 px-3 text-gray-500 hover:bg-gray-50"
        aria-label="Otwórz listę"
        @click="toggleOpen"
      >
        <svg class="h-4 w-4 transition-transform" :class="{ 'rotate-180': open }" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <teleport to="body">
      <div
        v-if="open"
        ref="panelRef"
        class="z-[10050] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
        :style="panelStyle"
        role="listbox"
      >
        <div class="border-b border-gray-100 p-2">
          <input
            ref="searchRef"
            v-model="query"
            type="search"
            class="form-input w-full text-sm"
            placeholder="Szukaj strony lub kontekstu…"
            autocomplete="off"
            @keydown.down.prevent="moveHighlight(1)"
            @keydown.up.prevent="moveHighlight(-1)"
            @keydown.enter.prevent="selectHighlighted"
            @keydown.escape.prevent="close"
          >
        </div>

        <ul class="max-h-64 overflow-y-auto py-1">
          <li v-if="filteredOptions.length === 0" class="px-4 py-6 text-center text-sm text-gray-500">
            Brak wyników dla „{{ query }}”
          </li>
          <li
            v-for="(page, index) in filteredOptions"
            :key="page.id"
            role="option"
            :aria-selected="page.id === numericValue"
          >
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors"
              :class="index === highlightedIndex ? 'bg-indigo-50' : 'hover:bg-gray-50'"
              @click="selectPage(page)"
              @mouseenter="highlightedIndex = index"
            >
              <span class="min-w-0 flex-1">
                <span class="flex flex-wrap items-center gap-2">
                  <span class="font-medium text-gray-900">
                    {{ page.title || page.label }}
                  </span>
                  <site-context-badge
                    :tag="page.context_tag"
                    :color-token="page.context_color"
                  />
                </span>
                <span v-if="page.subtitle" class="mt-0.5 block text-xs text-gray-500">
                  {{ page.subtitle }}
                </span>
              </span>
              <svg
                v-if="page.id === numericValue"
                class="h-5 w-5 shrink-0 text-indigo-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
              </svg>
            </button>
          </li>
        </ul>
      </div>
    </teleport>

    <div v-if="errors.length" class="form-error">
      {{ errors[0] }}
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { v4 as uuid } from 'uuid';
import SiteContextBadge from '@/Shared/SiteContextBadge.vue';

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: null,
  },
  options: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: 'Wybierz stronę…',
  },
  required: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Array,
    default: () => [],
  },
  inputId: {
    type: String,
    default: () => `page-link-select-${uuid()}`,
  },
});

const emit = defineEmits(['update:modelValue']);

const rootRef = ref(null);
const panelRef = ref(null);
const searchRef = ref(null);
const open = ref(false);
const query = ref('');
const highlightedIndex = ref(0);
const panelStyle = ref({ position: 'fixed', left: '0', top: '0', width: '0' });

let cleanupFloating = null;

const numericValue = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined || props.modelValue === '') {
    return null;
  }
  return Number(props.modelValue);
});

const normalizedOptions = computed(() =>
  props.options.map((page) => ({
    ...page,
    title: page.title || page.label,
    context_tag: page.context_tag ?? page.contextTag ?? '',
    context_color: page.context_color ?? page.contextColor ?? 'code-medical-center',
    subtitle: page.subtitle ?? null,
  })),
);

const optionsById = computed(() =>
  Object.fromEntries(normalizedOptions.value.map((page) => [page.id, page])),
);

const selectedPage = computed(() => {
  if (numericValue.value == null) return null;
  return optionsById.value[numericValue.value] || null;
});

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');

const filteredOptions = computed(() => {
  const q = normalize(query.value.trim());
  if (!q) return normalizedOptions.value;

  return normalizedOptions.value.filter((page) => {
    const haystack = [
      page.title,
      page.label,
      page.context_tag,
      page.subtitle,
      page.slug,
      page.resource_kind,
      page.site_context,
    ]
      .filter(Boolean)
      .join(' ');

    return normalize(haystack).includes(q);
  });
});

watch(filteredOptions, () => {
  highlightedIndex.value = 0;
});

watch(open, async (isOpen) => {
  if (isOpen) {
    query.value = '';
    highlightedIndex.value = Math.max(
      0,
      filteredOptions.value.findIndex((page) => page.id === numericValue.value),
    );
    await nextTick();
    positionPanel();
    searchRef.value?.focus();
    if (rootRef.value && panelRef.value) {
      cleanupFloating = autoUpdate(rootRef.value, panelRef.value, positionPanel);
    }
  } else if (cleanupFloating) {
    cleanupFloating();
    cleanupFloating = null;
  }
});

async function positionPanel() {
  if (!rootRef.value || !panelRef.value) return;

  const { x, y } = await computePosition(rootRef.value, panelRef.value, {
    placement: 'bottom-start',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
  });

  panelStyle.value = {
    position: 'fixed',
    left: `${x}px`,
    top: `${y}px`,
    width: `${rootRef.value.offsetWidth}px`,
    zIndex: 10050,
  };
}

function toggleOpen() {
  open.value = !open.value;
}

function close() {
  open.value = false;
}

function clear() {
  emit('update:modelValue', null);
  close();
}

function selectPage(page) {
  emit('update:modelValue', page.id);
  close();
}

function selectHighlighted() {
  const page = filteredOptions.value[highlightedIndex.value];
  if (page) selectPage(page);
}

function moveHighlight(delta) {
  const len = filteredOptions.value.length;
  if (len === 0) return;
  highlightedIndex.value = (highlightedIndex.value + delta + len) % len;
}

function onDocumentClick(event) {
  if (!open.value) return;
  const root = rootRef.value;
  const panel = panelRef.value;
  if (root?.contains(event.target) || panel?.contains(event.target)) return;
  close();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  if (cleanupFloating) cleanupFloating();
});
</script>
